import React, { useContext } from 'react'
import SystemChart from '../../shared/components/SystemChart'
import { ChartView, DEFAULT_CHART_HEIGHT, RechartData, useLegendState } from '../../../chart-kit/shared'
import { getChartsEditingSettings } from '../../../chart-kit/editor'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import useAvailableLines from '../../shared/hooks/useAvailableLines'
import { getInitialPerformanceValueChart } from '../utils/initial-chart-config'
import { useTypedSelector } from 'src/State/RootReducer'
import { useParams } from 'react-router-dom'
import { useUserSettings } from 'src/Hooks'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { useUpdateEditedChartSettings } from '../../../chart-kit/editor/hooks'
import { SaveChartProps } from 'src/Libs/system-data-handler/types'
import useChartLayout from 'src/Libs/chart-kit/shared/hooks/use-chart-layout'
import { DASHBOARD_PERFORMANCE_VALUE_CHART_ID } from 'src/Libs/qa/ids'
import { useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'

interface Props {
  data: RechartData
  view: ChartView
  resolution?: ChartView
  handleSaveChart: (props: SaveChartProps) => void
  from: Moment
  to: Moment
  showForecast: boolean
}

/**
 * A default chart visualizing the performance values of a system
 */
const PerformanceValueChart: React.FC<Props> = ({ data, view, resolution, handleSaveChart, from, to, showForecast }) => {
  const powerValuesChartHeight = DEFAULT_CHART_HEIGHT
  const availableLines = useAvailableLines()
  const { userSettings } = useUserSettings()
  const { data: wallboxes } = useWallboxHardwareByStorage()
  const powermeters = useTypedSelector(store => store.storage.powermeterHardware)
  const chartSettingsList = getChartsEditingSettings(userSettings)
  const initialChartLayout = getInitialPerformanceValueChart(powerValuesChartHeight, availableLines, wallboxes, powermeters)
  const { chartLayout } = useChartLayout(chartSettingsList, initialChartLayout)
  const chartLayoutId = chartLayout.id ?? ''
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const userId = useTypedSelector(state => state.auth.userId)
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const { deleteChart } = useUpdateEditedChartSettings()
  const { saveLegendState, hideLegend } = useLegendState(`${chartLayoutId}`)

  const handleDeleteChart = (id: string | number): void => {
    if (!storageId) return
    deleteChart({
      chartId: id,
      chartSettingsList,
      userId,
      installationId: Number(installationId),
      storageSerialNumber: storageId,
    })
  }

  return (
    <SystemChart
      view={view}
      resolution={resolution}
      to={to}
      from={from}
      showForecast={showForecast}
      rechartData={data}
      loadingState={data.loadingState}
      customLayout={chartLayout}
      handleSaveChart={props => handleSaveChart({ customLayout: chartLayout, chartSettings: props })}
      handleDeleteChart={id => handleDeleteChart(id)}
      maxHeight={chartLayout.maxHeight}
      isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
      hideLegend={hideLegend}
      toggleHideLegend={() => saveLegendState()}
      initialChartLayout={initialChartLayout}
      dataQa={DASHBOARD_PERFORMANCE_VALUE_CHART_ID}
    />
  )
}

export default PerformanceValueChart
